import React, { useState, useEffect } from "react";
import {
  Paper,
  Grid,
  Typography,
  FormControlLabel,
  Switch,
  Tooltip,
  Button,
  TextField,
  MenuItem,
} from "@material-ui/core";
import clsx from "clsx";
import { useForm, FormProvider, Controller } from "react-hook-form";
import apiClient from "../../auth/apiClient";
import OneParkService from "../../services/OneParkService";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import LogoUpload from "./LogoUpload";
import EmailTemplateEditor from "./EmailTemplateEditor";
import useStyles from "./styles";

const oneParkService = new OneParkService(apiClient);

const RESTRICTED_SIGNUP_TOOLTIP =
  "When this setting is enabled, users will be required to enter the email and account number that is attached to their access holder to create their account. When this setting is disabled, anyone can sign up and create an account.";

const AUTO_BARCODE_TOOLTIP =
  "When this setting is enabled, users will automatically be assigned a barcode credential when their account is created.";

const MAX_EXPIRATION_DAYS = 7300; 

const convertToDays = (value, unit) => {
  if (unit === "days") return value;
  if (unit === "months") return value * 30;
  if (unit === "years") return value * 365;
};

const convertFromDays = (days) => {
  if (!days || days <= 0) return { value: 1, unit: "days" };
  if (days % 365 === 0) return { value: days / 365, unit: "years" };
  if (days % 30 === 0) return { value: days / 30, unit: "months" };
  return { value: days, unit: "days" };
};

const getMaxValueForUnit = (unit) => {
  if (unit === "days") return MAX_EXPIRATION_DAYS;
  if (unit === "months") return Math.floor(MAX_EXPIRATION_DAYS / 30); 
  if (unit === "years") return 20;
  return MAX_EXPIRATION_DAYS;
};

const OneParkAdminContainer = () => {
  const classes = useStyles();
  const { facilityID } = useCurrentFacility();
  const enqueueSnackbar = useEnqueueSnackbar();
  const [logoData, setLogoData] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [savedEmailConfig, setSavedEmailConfig] = useState({
    displayName: "",
    replyTo: ""
  });
  const [emailFieldsTouched, setEmailFieldsTouched] = useState(false);


  const methods = useForm({
    defaultValues: {
      inviteEmailSubject: "",
      inviteEmailBody: "",
      expiredCardEmailSubject: "",
      expiredCardEmailBody: "",
      restrictedSignup: false,
      autoGenerateBarcode: false,
      pointValidityNeverExpires: true,
      pointValidityExpirationValue: 1,
      pointValidityExpirationUnit: "days",
      ccReplyEnabled: false,
      displayName: "",
      replyTo: "",
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { dirtyFields },
  } = methods;
 
  const pointValidityNeverExpires = watch("pointValidityNeverExpires");
  const pointValidityExpirationUnit = watch("pointValidityExpirationUnit");
  
  const replyTo = watch("replyTo");
  const displayName = watch("displayName");

  useEffect(() => {
    const hasReplyTo = replyTo && replyTo.trim() !== "";
    const hasDisplayName = displayName && displayName.trim() !== "";

    if ((hasReplyTo || hasDisplayName) && !emailFieldsTouched) {
      setEmailFieldsTouched(true);
    }

    if (hasReplyTo && !hasDisplayName) {
      methods.setError("displayName", { type: "required", message: "Display name is required" });
    } else if (emailFieldsTouched && !hasReplyTo && !hasDisplayName) {

      methods.setError("displayName", { type: "required", message: "Display name is required" });
    } else {
      methods.clearErrors("displayName");
    }

    if (hasDisplayName && !hasReplyTo) {
      methods.setError("replyTo", { type: "required", message: "Reply-to email is required" });
    } else if (emailFieldsTouched && !hasReplyTo && !hasDisplayName) {
      const currentError = methods.formState.errors.replyTo;
      if (currentError?.type !== "format") {
        methods.setError("replyTo", { type: "required", message: "Reply-to email is required" });
      }
    } else {
      const currentError = methods.formState.errors.replyTo;
      if (currentError?.type === "required") {
        methods.clearErrors("replyTo");
      }
    }
  }, [replyTo, displayName, emailFieldsTouched]);


  
  useEffect(() => {
    if (!facilityID) return;

    const fetchSettings = async () => {
      try {
        const response = await oneParkService.getAdminSettings(facilityID);
        const data = response.data;

        const neverExpire = data?.pointsValidity?.neverExpire ?? true;
        const expirationDays = data?.pointsValidity?.expirationDays;
        const { value: expValue, unit: expUnit } = convertFromDays(expirationDays);
        
        setSavedEmailConfig({
          displayName: data?.emailConfiguration?.displayName ?? "",
          replyTo: data?.emailConfiguration?.email ?? "",
        });

        if ((data?.emailConfiguration?.displayName) || (data?.emailConfiguration?.email)) {
          setEmailFieldsTouched(true);
        }

        reset({
          inviteEmailSubject: data?.inviteEmailTemplate?.subject || "",
          inviteEmailBody: data?.inviteEmailTemplate?.body || "",
          expiredCardEmailSubject: data?.expiredCardEmailTemplate?.subject || "",
          expiredCardEmailBody: data?.expiredCardEmailTemplate?.body || "",
          restrictedSignup: data?.restrictedSignup || false,
          autoGenerateBarcode: data?.autoGenerateBarcodeCredential || false,
          pointValidityNeverExpires: neverExpire,
          pointValidityExpirationValue: !neverExpire && expirationDays ? expValue : 1,
          pointValidityExpirationUnit: !neverExpire && expirationDays ? expUnit : "days",
          ccReplyEnabled: data?.emailConfiguration?.ccReplyEnabled ?? false,
          displayName: data?.emailConfiguration?.displayName ?? "",
          replyTo: data?.emailConfiguration?.email ?? "",
        });
      } catch {
        enqueueSnackbar("Failed to load admin settings", {
          variant: "error",
          tag: "FailedToLoadOneParkSettings",
        });
      }

      try {
        const logoResponse = await oneParkService.getLogo(facilityID);
        const logoUrl = logoResponse?.data?.url;
        if (logoUrl) {
          setLogoPreview(logoUrl);
        }
      } catch {
        // 404 means no logo uploaded yet � not an error
      }
    };

    fetchSettings();
  }, [facilityID]);

  const handleLogoChange = (logo) => {
    setLogoData(logo);
  };

  const onSubmit = async (data) => {
    try {
      const hasLogoChange = !!logoData?.file;
      const hasSettingsChange = Object.keys(dirtyFields).length > 0;

      if (!hasLogoChange && !hasSettingsChange) return;

      if (hasLogoChange) {
        const formData = new FormData();
        formData.append("file", logoData.file);
        const logoResponse = await oneParkService.uploadLogo(facilityID, formData);
        if (logoResponse?.data?.url) {
          setLogoPreview(logoResponse.data.url);
        }
      }

      const promises = [];

      if (hasSettingsChange) {
        const payload = {
          inviteEmailTemplate: { subject: data.inviteEmailSubject, body: data.inviteEmailBody },
          expiredCardEmailTemplate: { subject: data.expiredCardEmailSubject, body: data.expiredCardEmailBody },
          restrictedSignup: data.restrictedSignup,
          autoGenerateBarcodeCredential: data.autoGenerateBarcode,
          pointsValidity: {
            neverExpire: data.pointValidityNeverExpires,
            expirationDays:
              data.pointValidityNeverExpires
                ? null
                : convertToDays(
                    data.pointValidityExpirationValue,
                    data.pointValidityExpirationUnit
                  ),
          },
          emailConfiguration: {
            ccReplyEnabled: data.ccReplyEnabled,
            displayName: data.displayName,
            email: data.replyTo,
          },
        };
        promises.push(
          oneParkService.saveAdminSettings(facilityID, payload)
            .then(() => ({ type: "settings", success: true }))
            .catch(() => ({ type: "settings", success: false }))
        );
      }
      
      setSavedEmailConfig({
              displayName: data.displayName,
              replyTo: data.replyTo,
      })

      const results = await Promise.all(promises);
      const failed = results.filter((r) => !r.success).map((r) => r.type);

      if (failed.length === 0) {
        enqueueSnackbar("Settings saved successfully", {
          variant: "success",
          tag: "OneParkSettingsSaved",       
        });
      } else {
        enqueueSnackbar(`Failed to save ${failed.join(" and ")}`, {
          variant: "error",
          tag: "FailedToSaveOneParkSettings",
        });
      }
    } catch {
      enqueueSnackbar("Failed to save settings", { 
        variant: "error",
        tag: "FailedToSaveOneParkSettings",
      });
    }
  };

  return (
    <div className={clsx("onepark-admin-container", classes.root)}>
      <Paper className={clsx(classes.paper)}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <LogoUpload
                  onLogoChange={handleLogoChange}
                  initialPreview={logoPreview}
                />
              </Grid>

              <Grid item xs={12} md={9}>
                <div className={classes.section}>
                  <EmailTemplateEditor
                    title="Invite E-Mail"
                    subjectFieldName="inviteEmailSubject"
                    bodyFieldName="inviteEmailBody"
                    control={control}
                  />
                </div>

                <div className={classes.section}>
                  <EmailTemplateEditor
                    title="Expired Card E-Mail"
                    subjectFieldName="expiredCardEmailSubject"
                    bodyFieldName="expiredCardEmailBody"
                    control={control}
                  />
                </div>
              </Grid>

              <Grid item xs={false} md={3} implementation="css" />
              <Grid item xs={12} md={9} className="email-configuration-section">
                <Typography variant="subtitle1">
                  Email Configuration
                </Typography>

                <Grid container spacing={2}>
                  <Grid item>
                    <FormControlLabel
                      data-testid="cc-reply-enabled-toggle"
                      className={classes.cc_reply_enabled}
                      control={
                        <Controller
                          name="ccReplyEnabled"
                          control={control}
                          render={({ field }) => (
                              <Switch
                                data-testid="cc-reply-enabled-switch"
                                checked={field.value}
                                disabled={!watch("replyTo") || watch("replyTo").trim() === ""}
                                onChange={(e) => field.onChange(e.target.checked)}
                                color="primary"
                              />
                            )}
                        />
                      }
                      label="CC Reply Enabled"
                    />
                  </Grid>

                  <Grid item data-testid="display-name-input" className="display-name-input">
                    <Controller
                      name="displayName"
                      control={control}
                      rules={{
                        validate: (value) => {
                          const replyToValue = methods.getValues("replyTo");
                          if (replyToValue && replyToValue.trim() !== "" && (!value || value.trim() === "")) {
                            return "Display name is required";
                          }
                          return true;
                        },
                        maxLength: { value: 200, message: "Display name must not exceed 200 characters" },
                      }}
                      render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Display Name"
                        size="small"
                        variant="outlined"
                        error={!!error}
                        helperText={error?.message || " "}
                        onBlur={(e) => {
                          field.onBlur();
                          const value = e.target.value;
                          if ((!value || value.trim() === "") && savedEmailConfig.displayName) {
                            field.onChange(savedEmailConfig.displayName);
                          }
                        }}
                      />
                      )}
                    />
                 </Grid>

                  <Grid item data-testid="reply-to-input" className="reply-to-input">
                    <Controller
                      name="replyTo"
                      control={control}
                      rules={{
                        validate: (value) => {
                          const displayNameValue = methods.getValues("displayName");
                          if (displayNameValue && displayNameValue.trim() !== "" && (!value || value.trim() === "")) {
                            return "Reply-to email is required";
                          }
                          if (value && value.trim() !== "" && !/^[^@]+@[^@]+\.[^@]+$/.test(value)) {
                            return "Please enter a valid email address";
                          }
                          return true;
                        },
                        maxLength: { value: 320, message: "Email must not exceed 320 characters" },
                      }}
                      render={({ field, fieldState: { error } }) => (
                       <TextField
                          {...field}
                          label="Reply To"
                          size="small"
                          variant="outlined"
                          error={!!error}
                          helperText={error?.message || "reply-to email address"}
                          onChange={(e) => {
                            field.onChange(e);
                            const val = e.target.value;
                            if (val && val.trim() !== "" && !/^[^@]+@[^@]+\.[^@]+$/.test(val)) {
                              methods.setError("replyTo", { type: "format", message: "Please enter a valid email address" });
                            } else {
                              methods.clearErrors("replyTo");
                            }
                          }}
                          onBlur={(e) => {
                            field.onBlur();
                            const value = e.target.value;
                            if (!value || value.trim() === "" || !/^[^@]+@[^@]+\.[^@]+$/.test(value)) {
                              field.onChange(savedEmailConfig.replyTo);
                              const revertedValue = savedEmailConfig.replyTo;
                              const currentDisplayName = methods.getValues("displayName");
                              const shouldShowError =
                                (currentDisplayName && currentDisplayName.trim() !== "" && (!revertedValue || revertedValue.trim() === "")) ||
                                (emailFieldsTouched && (!revertedValue || revertedValue.trim() === ""));
                              if (shouldShowError) {
                                methods.setError("replyTo", { type: "required", message: "Reply-to email is required" });
                              } else {
                                methods.clearErrors("replyTo");
                              }
                            }
                        }}
                      />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={false} md={3} />
              <Grid item xs={12} md={9}>
                <Tooltip arrow title={RESTRICTED_SIGNUP_TOOLTIP}>
                  <FormControlLabel
                    data-testid="restricted-signup-toggle"
                    className="restricted-signup"
                    control={
                      <Controller
                        name="restrictedSignup"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            data-testid="restricted-signup-switch"
                            checked={field.value}
                            onChange={(e) =>
                              field.onChange(e.target.checked)
                            }
                            color="primary"
                          />
                        )}
                      />
                    }
                    label="Restricted Signup"
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={false} md={3} implementation="css"  />
              <Grid item xs={12} md={9}>
                <Tooltip arrow title={AUTO_BARCODE_TOOLTIP}>
                  <FormControlLabel
                    data-testid="auto-barcode-toggle"
                    className="auto-barcode"
                    control={
                      <Controller
                        name="autoGenerateBarcode"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            data-testid="auto-barcode-switch"
                            checked={field.value}
                            onChange={(e) =>
                              field.onChange(e.target.checked)
                            }
                            color="primary"
                          />
                        )}
                      />
                    }
                    label="Auto Generate Barcode Credential"
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={false} md={3} implementation="css" />
              <Grid item xs={12} md={9} className="points-validity-configuration-section">
                <Typography variant="subtitle1" className={classes.pointsValidityTitle}>
                  Points Validity Configuration
                </Typography>

                <FormControlLabel
                  data-testid="point-validity-never-expire"
                  control={
                    <Controller
                      name="pointValidityNeverExpires"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) =>
                            field.onChange(e.target.checked)
                          }
                          color="primary"
                        />
                      )}
                    />
                  }
                  label="Never Expire"
                />

                {!pointValidityNeverExpires && (
                    <div data-testid="point-validity-expiration-days" className="point-validity" style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "nowrap", marginTop: 8 }}>
                      <Controller
                        name="pointValidityExpirationValue"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            size="small"
                            variant="outlined"
                            value={field.value ?? 1}
                            onChange={(e) => {
                              let val = e.target.value;
                              if (val === "") return field.onChange("");
                              const max = getMaxValueForUnit(pointValidityExpirationUnit);
                              field.onChange(Math.min(max, Math.max(1, Number(val))));
                            }}
                            inputProps={{ min: 1, max: getMaxValueForUnit(pointValidityExpirationUnit) }}
                            style={{ width: 80 }}
                            helperText={`1 – ${getMaxValueForUnit(pointValidityExpirationUnit)} ${pointValidityExpirationUnit}`}
                          />
                        )}
                      />

                      <Controller
                        name="pointValidityExpirationUnit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            size="small"
                            variant="outlined"
                            style={{ width: 120 }}
                            helperText=""
                            SelectProps={{
                              MenuProps: {
                                getContentAnchorEl: null, 
                              },
                            }}
                          >
                          <MenuItem value="days" style={{ justifyContent: "center", cursor: "pointer" }}> Days </MenuItem>
                          <MenuItem value="months" style={{ justifyContent: "center", cursor: "pointer" }}> Months </MenuItem>
                          <MenuItem value="years" style={{ justifyContent: "center", cursor: "pointer" }}>  Years </MenuItem>

                          </TextField>
                        )}
                      />
                    </div>
                )}
              </Grid>

              <Grid item xs={false} md={3} />
              <Grid item xs={12} md={9} className={classes.saveButtonContainer}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  data-testid="save-button"
                  className="onepark-admin-save-btn"
                >
                  SAVE
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Paper>
    </div>
  );
};

export default OneParkAdminContainer;